"use client";

import { type ReactNode, useEffect, useState } from "react";
import { getNavLoading, subscribeNavLoading } from "@/components/navLoadingStore";

/**
 * Wraps the board's results card. While a navigation is in flight (a search or
 * filter change), it dims the current results and floats a "Finding jobs…"
 * spinner over them — so the 2–3s server round-trip never looks frozen. The old
 * results stay readable underneath until the new ones swap in on commit.
 */
export function ResultsLoadingOverlay({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(getNavLoading());
    return subscribeNavLoading(setLoading);
  }, []);

  return (
    <div className="relative">
      <div
        className={`transition-opacity duration-200 ${
          loading ? "pointer-events-none select-none opacity-40" : "opacity-100"
        }`}
      >
        {children}
      </div>
      {loading ? (
        <div
          aria-live="polite"
          className="pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-16 sm:pt-24"
        >
          <div className="flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white/95 px-4 py-2 shadow-md backdrop-blur-sm">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-600" />
            <span className="text-[13px] font-medium text-ink">
              Finding jobs…
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
